import type { JSX } from 'react';
import { generatePath } from 'react-router';

import { Button, Link } from 'Components/Buttons/Buttons';
import { Text } from 'Components/Text/Text';
import { TPRM_SERVICE_DASHBOARD, UCRM_USE_CASE_DASHBOARD } from 'Config/PathTemplates';
import { AssessmentSubjectBase } from 'Models/AssessmentSubject';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';

import styles from './CommonAssessment.module.css';

export interface CommonAssessmentProps {
    assessmentSubject: AssessmentSubjectBase;
    module: DueDiligenceModule;
    onSelectLinkToParent: () => void;
    onSelectUnlinkFromParent: () => void;
}

/** Displays information about whether an assessment subject is sharing due diligence assessments with another assessment subject. Renders buttons that allow the user to update this setting. */
export const CommonAssessment = (props: CommonAssessmentProps): JSX.Element => {
    const editButton = ((): JSX.Element | undefined => {
        const hasParent = props.assessmentSubject.assessment_workflow_setup.common_assessment_parent;
        const hasChildren = props.assessmentSubject.assessment_workflow_setup.common_assessment_children.length > 0;

        if (hasParent) {
            return (
                <div>
                    <Button variant="linkText" onClick={props.onSelectUnlinkFromParent} size="lg">
                        Stop using shared due diligence assessments
                    </Button>
                </div>
            );
        } else if (!hasParent && !hasChildren) {
            return (
                <div>
                    <Button variant="linkText" onClick={props.onSelectLinkToParent} size="lg">
                        Use shared due diligence assessments
                    </Button>
                </div>
            );
        } else {
            return undefined;
        }
    })();

    return (
        <>
            {getCommonAssessmentMessage(props.assessmentSubject, props.module)}
            {editButton && <div>{editButton}</div>}
        </>
    );
};

const getCommonAssessmentMessage = (assessmentSubject: AssessmentSubjectBase, module: DueDiligenceModule): JSX.Element => {
    const parent = assessmentSubject.assessment_workflow_setup.common_assessment_parent;
    const children = assessmentSubject.assessment_workflow_setup.common_assessment_children;
    const assessmentSubjectDashboardPath = module === ProductModule.UCRM ? UCRM_USE_CASE_DASHBOARD : TPRM_SERVICE_DASHBOARD;

    if (parent) {
        return (
            <>
                <div className={styles.textAboveLinkButton}>
                    <Text variant="Text2" color="white">
                        {module === ProductModule.UCRM ? 'This use case is using the shared due diligence assessments provided by:' : 'This third-party service is using the shared due diligence assessments provided by:'}
                    </Text>
                </div>
                <div>
                    <Link to={generatePath(assessmentSubjectDashboardPath, { third_party_id: parent.vendor_id, service_id: parent.id })} size="lg">
                        {parent.name}
                    </Link>
                </div>
            </>
        );
    } else if (children.length > 0) {
        const sortedChildren = children.sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
        return (
            <>
                <div className={styles.textAboveLinkButton}>
                    <Text variant="Text2" color="white">
                        {module === ProductModule.UCRM ? 'This use case is sharing its due diligence assessments with:' : 'This third-party service is sharing its due diligence assessments with:'}
                    </Text>
                </div>
                {sortedChildren.map((child) => {
                    return (
                        <div key={child.id}>
                            <Link to={generatePath(assessmentSubjectDashboardPath, { third_party_id: child.vendor_id, service_id: child.id })} size="lg">
                                {child.name}
                            </Link>
                        </div>
                    );
                })}
            </>
        );
    }
    return (
        <div className={styles.textAboveLinkButton}>
            <Text variant="Text2" color="white">
                {module === ProductModule.UCRM ? 'This use case is not using shared due diligence assessments.' : 'This third-party service is not using shared due diligence assessments.'}
            </Text>
        </div>
    );
};
